import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
} from "@/components/ui/form";
import { useSignConsentMutation } from "@/features/consent/api/upsertConsentSignature";
import { hasUserSignedConsent } from "@/features/consent/hooks/useConsent";
import { useProfileQuery } from "@/features/profile/api/selectProfile";
import type {
	ConsentSignatureT,
	ConsentViewT,
} from "@/types/database/consents";
import { useAuth } from "@/utils/auth";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import {
	AlertCircle,
	ArrowLeft,
	ClipboardSignature,
	Loader2,
	Trash2,
	UserCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useIsMyDiary from "../patients/hooks/useIsMyDiary";
import { useUnsignConsentMutation } from "./api/deleteConsentSignature";

interface ConsentFormProps {
	consent: ConsentViewT;
	diaryId: string;
	onSignSuccess?: () => void;
	onUnsignSuccess?: () => void;
}

interface SignatureFormValues {
	agreement: boolean;
}

// Define a type for the signature as it comes from the view with additional fields
interface ExtendedSignature extends ConsentSignatureT {
	name?: string;
	date_of_birth?: string;
}

export function ConsentForm({
	consent,
	diaryId,
	onSignSuccess,
	onUnsignSuccess,
}: ConsentFormProps) {
	const auth = useAuth();
	const [ipAddress, setIpAddress] = useState<string>("");

	const { isMyDiary } = useIsMyDiary(diaryId);
	// Get the IP address on component mount
	useEffect(() => {
		const getIpAddress = async () => {
			try {
				const response = await fetch("https://api.ipify.org?format=json");
				const data = await response.json();
				setIpAddress(data.ip);
			} catch (error) {
				console.error("Failed to fetch IP address:", error);
				setIpAddress("unknown");
			}
		};

		getIpAddress();
	}, []);

	const signConsentMutation = useSignConsentMutation({
		onSuccess: () => {
			if (onSignSuccess) {
				onSignSuccess();
			}
		},
	});

	const unsignConsentMutation = useUnsignConsentMutation({
		onSuccess: () => {
			if (onUnsignSuccess) {
				onUnsignSuccess();
			}
		},
	});

	// Early return if no consent or user
	if (!consent || !auth.id) return null;

	// Ensure signatures is an array of ExtendedSignature
	const signatures = Array.isArray(consent.signatures)
		? (consent.signatures as unknown as ExtendedSignature[])
		: [];

	// Check if the current user has already signed using the new utility function
	const hasCurrentUserSigned = hasUserSignedConsent(consent);

	const form = useForm<SignatureFormValues>({
		defaultValues: {
			agreement: false,
		},
	});

	const handleSignConsent = async (values: SignatureFormValues) => {
		if (!consent.consent_id || !auth.id || !values.agreement) return;

		await signConsentMutation.mutateAsync({
			consent_id: consent.consent_id,
			user_id: auth.id,
			ip_address: ipAddress,
			diary_id: diaryId,
		});
	};

	const handleUnsignConsent = async () => {
		if (!consent.consent_id || !auth.id) return;

		await unsignConsentMutation.mutateAsync({
			consent_id: consent.consent_id,
			user_id: auth.id,
			diary_id: diaryId,
		});
	};

	// Helper function to sort and format signatures
	const renderSignatures = () => {
		// Get all required signatures (including unsigned ones)
		const allRequiredSignatures = signatures;

		if (allRequiredSignatures.length === 0) {
			return null;
		}

		return (
			<div className="space-y-6 mb-6">
				<h3 className="font-semibold text-lg">Signatures</h3>
				<div className="space-y-6">
					{allRequiredSignatures.map((sig) => (
						<SignatureItem
							key={sig.signature_id}
							signature={sig}
							isCurrentUser={sig.user_id === auth.id}
						/>
					))}
				</div>
			</div>
		);
	};

	// Navigation buttons that are always visible
	const renderNavigationButtons = () => (
		<Button
			size="lg"
			variant={hasCurrentUserSigned ? "default" : "secondary"}
			className="no-underline"
			asChild
		>
			<Link
				className="no-underline"
				to={
					auth.roles.isClinician
						? "/patients/$diaryId"
						: "/diaries/$diaryId/home/new"
				}
				params={{
					diaryId: diaryId || consent.diary_id || "",
				}}
			>
				<ArrowLeft className="w-5 h-5" />
				Back to Diary
			</Link>
		</Button>
	);

	return (
		<div className="mt-10 pt-10 border-muted border-t" id="consent-signature">
			{renderSignatures()}

			{hasCurrentUserSigned ? (
				<div className="space-y-4">
					<div className="flex items-center gap-2 text-indigo-700">
						<UserCheck className="w-5 h-5" />
						<p className="my-0 py-0 font-medium">
							Your signature has been recorded
						</p>
					</div>
					<p className="text-muted-foreground hide-on-print">
						You have successfully signed this consent form.
					</p>

					<div className="flex md:flex-row flex-col items-center gap-4">
						{renderNavigationButtons()}
						<Button
							size="lg"
							variant="outline"
							onClick={handleUnsignConsent}
							disabled={unsignConsentMutation.isPending}
						>
							{unsignConsentMutation.isPending ? (
								<>
									<Loader2 size={20} className="animate-spin" />
									Removing...
								</>
							) : (
								<>
									<Trash2 size={20} className="" />
									Remove Signature
								</>
							)}
						</Button>
					</div>
				</div>
			) : (
				<div className="space-y-6">
					<div className="flex items-center gap-2 text-gray-600">
						<AlertCircle className="w-5 h-5" />
						<p className="my-0 py-0 font-medium">Your signature is required</p>
					</div>

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSignConsent)}
							className="space-y-6 hide-on-print"
						>
							<FormField
								control={form.control}
								name="agreement"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center space-x-3 space-y-0 bg-slate-50 p-4 border rounded-md w-full">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
										<FormLabel className="font-normal text-muted-foreground text-sm text-wrap">
											{isMyDiary
												? "I confirm that I have read, understood, and agree to the terms outlined in this consent form and I have not undergone a COVID-19 vaccination within the last 2 weeks."
												: "I confirm that I, and the patient have read, understood, and agree to the terms outlined in this consent form."}
										</FormLabel>
									</FormItem>
								)}
							/>

							<div className="flex md:flex-row flex-col flex-wrap items-center gap-4">
								<Button
									size="lg"
									type="submit"
									disabled={
										signConsentMutation.isPending || !form.watch("agreement")
									}
								>
									{signConsentMutation.isPending ? (
										<>
											<Loader2 size={20} className="animate-spin" />
											Signing...
										</>
									) : (
										<>
											<ClipboardSignature size={20} className="mr-1.5" />
											Sign Consent
										</>
									)}
								</Button>
								{renderNavigationButtons()}
							</div>
						</form>
					</Form>
				</div>
			)}
		</div>
	);
}

// Separate component for rendering signature items
function SignatureItem({
	signature,
	isCurrentUser,
}: {
	signature: ExtendedSignature;
	isCurrentUser: boolean;
}) {
	// Get the current user's profile if this is the current user's signature
	const { data: profile } = useProfileQuery();

	// Check if the signature is complete
	const isSignatureComplete = Boolean(signature.signed_at);

	// Format the signature date safely
	const formattedSignatureDate = signature.signed_at
		? format(new Date(signature.signed_at), "PPP 'at' p")
		: "Unknown date";

	return (
		<div className="space-y-2">
			<div className="flex items-center gap-2">
				{isSignatureComplete ? (
					<UserCheck className="flex-shrink-0 w-5 h-5 text-indigo-600" />
				) : (
					<AlertCircle className="flex-shrink-0 w-5 h-5 text-gray-400" />
				)}
				<div className="font-medium text-gray-700">
					{isCurrentUser ? "Your signature" : "Signature"}
				</div>
			</div>

			<div className="space-y-2">
				<div className="flex items-center border-gray-300 border-b h-8">
					<span className="font-semibold text-gray-800 text-base">
						{isCurrentUser
							? profile?.name || "You"
							: signature.name || "Unknown User"}
					</span>
				</div>

				{isSignatureComplete ? (
					<div className="flex items-center border-gray-300 border-b h-8">
						<span className="text-gray-700 text-base">
							Signed on {formattedSignatureDate}
						</span>
					</div>
				) : (
					<div className="flex items-center border-gray-300 border-b h-8">
						<span className="text-gray-400 text-base italic">
							Not yet signed
						</span>
					</div>
				)}

				{signature.date_of_birth && !isCurrentUser && (
					<div className="flex items-center border-gray-300 border-b h-8">
						<span className="text-gray-700 text-base">
							DOB: {format(new Date(signature.date_of_birth), "PP")}
						</span>
					</div>
				)}

				{signature.ip_address && isSignatureComplete && (
					<div className="text-gray-500 text-xs">
						IP: {signature.ip_address}
					</div>
				)}
			</div>
		</div>
	);
}
